import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Users, Mail, Calendar, FileText, ArrowRight, Sparkles } from 'lucide-react';

const STEPS = [
  {
    title: 'Welcome to BrixLead!',
    subtitle: 'Let\'s get you set up in 4 easy steps',
    icon: Sparkles,
    content: 'welcome',
  },
  {
    title: 'Invite Your Team',
    subtitle: 'Add counselors and managers to your CRM',
    icon: Users,
    content: 'team',
    link: '/users',
    linkText: 'Go to Users →',
  },
  {
    title: 'Connect Email',
    subtitle: 'Set up email integration for sending emails to leads',
    icon: Mail,
    content: 'email',
    link: '/settings',
    linkText: 'Go to Settings →',
  },
  {
    title: 'Set Your Availability',
    subtitle: 'Configure your working hours for booking meetings',
    icon: Calendar,
    content: 'availability',
    link: '/time-availability',
    linkText: 'Set Availability →',
  },
  {
    title: 'Create Your First Lead',
    subtitle: 'Add a lead manually or import via CSV',
    icon: FileText,
    content: 'lead',
    link: '/leads',
    linkText: 'Go to Leads →',
  },
];

export function OnboardingPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());

  const step = STEPS[currentStep];
  const isLastStep = currentStep === STEPS.length - 1;

  const markDone = () => {
    setCompletedSteps(prev => new Set([...prev, currentStep]));
    if (isLastStep) {
      localStorage.setItem('onboardingComplete', 'true');
      navigate('/dashboard');
    } else {
      setCurrentStep(s => s + 1);
    }
  };

  const skipAll = () => {
    localStorage.setItem('onboardingComplete', 'true');
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center px-4">
      <div className="w-full max-w-2xl">
        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {STEPS.map((_, i) => (
            <div key={i} className={`h-2 rounded-full transition-all ${i === currentStep ? 'w-12 bg-[#6366F1]' : completedSteps.has(i) ? 'w-8 bg-emerald-400' : 'w-8 bg-slate-200'}`} />
          ))}
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="h-16 w-16 rounded-2xl bg-indigo-100 flex items-center justify-center mx-auto mb-6">
            <step.icon className="h-8 w-8 text-[#6366F1]" />
          </div>

          <h1 className="text-2xl font-bold text-slate-900 mb-2">{step.title}</h1>
          <p className="text-sm text-slate-500 mb-8">{step.subtitle}</p>

          {currentStep === 0 ? (
            <div className="space-y-3 text-left max-w-sm mx-auto mb-8">
              {STEPS.slice(1).map((s, i) => (
                <div key={i} className="flex items-center gap-3 text-sm text-slate-600">
                  <div className="h-6 w-6 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-[#6366F1]">{i + 1}</span>
                  </div>
                  {s.title}
                </div>
              ))}
            </div>
          ) : (
            <div className="mb-8">
              {step.link && (
                <button onClick={() => window.open(step.link, '_blank')}
                  className="text-sm text-[#6366F1] font-medium hover:underline mb-4 block mx-auto">
                  {step.linkText}
                </button>
              )}
              <p className="text-xs text-slate-400">You can always do this later from the sidebar</p>
            </div>
          )}

          <div className="flex items-center justify-center gap-3">
            {currentStep > 0 && (
              <button onClick={() => setCurrentStep(s => s - 1)}
                className="px-6 py-2.5 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">
                Back
              </button>
            )}
            <button onClick={markDone}
              className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold">
              {isLastStep ? (
                <><Check className="h-4 w-4" /> Finish Setup</>
              ) : currentStep === 0 ? (
                <><ArrowRight className="h-4 w-4" /> Let's Start</>
              ) : (
                <><Check className="h-4 w-4" /> Done, Next</>
              )}
            </button>
          </div>

          {currentStep > 0 && (
            <button onClick={skipAll} className="text-xs text-slate-400 hover:text-slate-600 mt-4 block mx-auto">
              Skip setup, go to dashboard
            </button>
          )}
        </div>

        {/* Step counter */}
        <p className="text-center text-xs text-slate-400 mt-4">
          Step {currentStep + 1} of {STEPS.length}
        </p>
      </div>
    </div>
  );
}
